/**
 * URL Parameter Validation Utilities
 * Provides validation and sanitization for URL parameters to prevent security issues
 */

/**
 * Validates and sanitizes a URL parameter value
 * @param {string} value - The parameter value to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxLength - Maximum allowed length
 * @param {RegExp} options.pattern - Allowed pattern (regex)
 * @param {boolean} options.allowEmpty - Whether empty values are allowed
 * @returns {string|null} - Sanitized value or null if invalid
 */
export function validateUrlParam(value, options = {}) {
  const { maxLength = 500, pattern = null, allowEmpty = false } = options;

  // Handle null/undefined
  if (value === null || value === undefined) {
    return allowEmpty ? null : null;
  }

  // Convert to string
  const stringValue = String(value).trim();

  // Check empty
  if (!stringValue && !allowEmpty) {
    return null;
  }

  // Check length
  if (stringValue.length > maxLength) {
    // eslint-disable-next-line no-console
    console.warn(`URL parameter exceeds max length: ${maxLength}`);
    return null;
  }

  // Check pattern
  if (pattern && !pattern.test(stringValue)) {
    // eslint-disable-next-line no-console
    console.warn(`URL parameter does not match required pattern`);
    return null;
  }

  // Basic XSS prevention - remove script tags and dangerous characters
  const sanitized = stringValue
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '');

  return sanitized;
}

/**
 * Validates an encrypted data parameter
 * @param {string} encryptedData - Encrypted data string
 * @returns {string|null} - Validated encrypted data or null
 */
export function validateEncryptedData(encryptedData) {
  if (!encryptedData) return null;

  // Encrypted data should be base64-like string
  // Basic validation: alphanumeric, +, /, = characters
  const encryptedDataPattern = /^[A-Za-z0-9+/=]+$/;

  return validateUrlParam(encryptedData, {
    maxLength: 2000,
    pattern: encryptedDataPattern,
    allowEmpty: false,
  });
}

/**
 * Validates a token parameter
 * @param {string} token - Token string
 * @returns {string|null} - Validated token or null
 */
export function validateToken(token) {
  if (!token) return null;

  // Token should be alphanumeric with possible dashes/underscores
  const tokenPattern = /^[A-Za-z0-9._-]+$/;

  return validateUrlParam(token, {
    maxLength: 500,
    pattern: tokenPattern,
    allowEmpty: false,
  });
}

/**
 * Validates an ID parameter (numeric or alphanumeric)
 * @param {string} id - ID string
 * @param {boolean} numericOnly - Whether ID must be numeric only
 * @returns {string|null} - Validated ID or null
 */
export function validateId(id, numericOnly = false) {
  if (!id) return null;

  const pattern = numericOnly ? /^\d+$/ : /^[A-Za-z0-9_-]+$/;

  return validateUrlParam(id, {
    maxLength: 100,
    pattern,
    allowEmpty: false,
  });
}

/**
 * Validates all URL parameters from a URL object
 * @param {URL} url - URL object
 * @returns {Object} - Validated parameters object
 */
export function validateUrlParams(url) {
  const params = {};

  // Validate encryptedData
  const encryptedData =
    url.searchParams.get('EncryptedData') ||
    url.searchParams.get('amp;EncryptedData');
  if (encryptedData) {
    params.encryptedData = validateEncryptedData(encryptedData);
  }

  // Validate token
  const token = url.searchParams.get('token');
  if (token) {
    params.token = validateToken(token);
  }

  // Validate calcId
  const calcId = url.searchParams.get('calcId');
  if (calcId) {
    params.calcId = validateId(calcId, true);
  }

  // Validate policyId
  const policyId = url.searchParams.get('policyId');
  if (policyId) {
    params.policyId = validateId(policyId);
  }

  // Validate referenceId
  const referenceId =
    url.searchParams.get('ReferenceNumber') ||
    url.searchParams.get('ReferenceId');
  if (referenceId) {
    params.referenceId = validateId(referenceId);
  }

  // Validate stepId
  const stepId =
    url.searchParams.get('steps') || url.searchParams.get('stepId');
  if (stepId) {
    params.stepId = validateId(stepId);
  }

  // Validate manualLogin (development only)
  if (process.env.NODE_ENV === 'development') {
    const manualLogin = url.searchParams.get('manualLogin');
    if (manualLogin) {
      params.manualLogin = validateUrlParam(manualLogin, {
        maxLength: 10,
        pattern: /^(true|false|1|0)$/i,
        allowEmpty: false,
      });
    }
  }

  return params;
}

/**
 * Sanitizes a URL to prevent XSS and other attacks
 * @param {string} urlString - URL string to sanitize
 * @returns {URL|null} - Sanitized URL object or null if invalid
 */
export function sanitizeUrl(urlString) {
  try {
    const url = new URL(urlString);

    // Validate and sanitize all parameters
    const validatedParams = validateUrlParams(url);

    // Create new URL with validated parameters only
    const sanitizedUrl = new URL(url.origin + url.pathname);

    // Add validated parameters
    Object.entries(validatedParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        sanitizedUrl.searchParams.set(key, value);
      }
    });

    return sanitizedUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Invalid URL:', error);
    return null;
  }
}
